/**
 * Authentication Screen
 * 
 * Provides login, signup, and password reset functionality.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AUTH_CONFIG } from '../config/auth';
import { Filter } from 'bad-words';
import { LegalDocumentModal } from './LegalDocumentModal';
import './AuthScreen.css';

type AuthMode = 'login' | 'signup' | 'reset';

export function AuthScreen(): React.ReactElement {
  const { signIn, signUp, resetPassword, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedContentUse, setAcceptedContentUse] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState<'terms' | 'privacy' | null>(null);
  
  // Profanity filter for display names
  const profanityFilter = new Filter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccessMessage('');
    setIsSubmitting(true);

    try {
      if (mode === 'login') {
        await signIn(email, password);
        // After successful login, redirect to the intended destination
        const redirectTo = searchParams.get('redirectTo') || '/worlds';
        navigate(redirectTo);
      } else if (mode === 'signup') {
        // Prevent signup if disabled
        if (!AUTH_CONFIG.allowSignUp) {
          throw new Error('Sign up is currently disabled');
        }
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        if (!displayName.trim()) {
          throw new Error('Display name is required');
        }
        if (profanityFilter.isProfane(displayName)) {
          throw new Error('Display name contains inappropriate language');
        }
        if (!acceptedTerms) {
          throw new Error('You must accept the Terms and Conditions');
        }
        if (!acceptedPrivacy) {
          throw new Error('You must accept the Privacy Policy');
        }
        if (!acceptedContentUse) {
          throw new Error('You must acknowledge that gameplay may be used for content creation');
        }
        await signUp(email, password, displayName);
        // After successful signup, redirect to verification or intended destination
        const redirectTo = searchParams.get('redirectTo') || '/verify';
        navigate(redirectTo);
      } else if (mode === 'reset') {
        await resetPassword(email);
        setSuccessMessage('Password reset email sent! Check your inbox.');
        setEmail('');
      }
    } catch (err: any) {
      // Error is handled by AuthContext
      console.error('Auth error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    // Prevent switching to signup if disabled
    if (newMode === 'signup' && !AUTH_CONFIG.allowSignUp) {
      return;
    }
    setMode(newMode);
    clearError();
    setSuccessMessage('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
    setAcceptedTerms(false);
    setAcceptedPrivacy(false);
    setAcceptedContentUse(false);
  };
  
  // Reset to login if signup is disabled and user is on signup mode
  React.useEffect(() => {
    if (mode === 'signup' && !AUTH_CONFIG.allowSignUp) {
      setMode('login');
    }
  }, [mode]);

  // Set background image via inline styles (CSS can't resolve public assets)
  // Offset to the right so the adventurer (on right side of image) is visible, not behind the centered container
  const backgroundStyle: React.CSSProperties = {
    backgroundImage: `url('/assets/backgrounds/auth-background.png')`,
    backgroundSize: 'cover', // Ensures full coverage on all devices
    backgroundPosition: '65% center', // Shift right to show adventurer (who is on right side of image)
    backgroundRepeat: 'no-repeat',
  };

  return (
    <div className="auth-screen" style={backgroundStyle}>
      <div className="auth-container">
        <div className="auth-header">
          <h1>Brynlore</h1>
          <p>Story-based adventures</p>
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
            onClick={() => switchMode('login')}
            type="button"
          >
            Sign In
          </button>
          {AUTH_CONFIG.allowSignUp && (
            <button
              className={`auth-tab ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => switchMode('signup')}
              type="button"
            >
              Sign Up
            </button>
          )}
        </div>
        
        {!AUTH_CONFIG.allowSignUp && AUTH_CONFIG.showSignUpDisabledMessage && (
          <div className="auth-info-message">
            {AUTH_CONFIG.signUpDisabledMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'signup' && (
            <div className="auth-field">
              <label htmlFor="displayName">Display Name</label>
              <input
                id="displayName"
                type="text"
                placeholder="Your character name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
            />
          </div>

          {mode !== 'reset' && (
            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isSubmitting}
                minLength={mode === 'signup' ? 6 : undefined}
              />
            </div>
          )}

          {mode === 'signup' && (
            <div className="auth-field">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isSubmitting}
                minLength={6}
              />
            </div>
          )}

          {mode === 'signup' && (
            <div className="auth-legal-section">
              <div className="auth-checkbox-field">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={isSubmitting}
                />
                <label htmlFor="acceptTerms">
                  I agree to the{' '}
                  <button
                    type="button"
                    className="auth-legal-link"
                    onClick={() => setShowLegalModal('terms')}
                    disabled={isSubmitting}
                  >
                    Terms and Conditions
                  </button>
                </label>
              </div>
              <div className="auth-checkbox-field">
                <input
                  id="acceptPrivacy"
                  type="checkbox"
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  disabled={isSubmitting}
                />
                <label htmlFor="acceptPrivacy">
                  I agree to the{' '}
                  <button
                    type="button"
                    className="auth-legal-link"
                    onClick={() => setShowLegalModal('privacy')}
                    disabled={isSubmitting}
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>
              <div className="auth-checkbox-field">
                <input
                  id="acceptContentUse"
                  type="checkbox"
                  checked={acceptedContentUse}
                  onChange={(e) => setAcceptedContentUse(e.target.checked)}
                  disabled={isSubmitting}
                />
                <label htmlFor="acceptContentUse">
                  I understand that the quests I complete may be used for the creation of anonymous content, such as podcasts and videos.
                </label>
              </div>
            </div>
          )}

          {mode === 'login' && (
            <button
              type="button"
              className="auth-link"
              onClick={() => switchMode('reset')}
              disabled={isSubmitting}
            >
              Forgot password?
            </button>
          )}

          {error && <div className="auth-error">{error}</div>}
          {successMessage && <div className="auth-success">{successMessage}</div>}

          <button
            type="submit"
            className="auth-submit"
            disabled={
              isSubmitting || 
              !email || 
              (mode !== 'reset' && !password) || 
              (mode === 'signup' && (!displayName || !acceptedTerms || !acceptedPrivacy || !acceptedContentUse))
            }
          >
            {isSubmitting
              ? 'Please wait...'
              : mode === 'login'
              ? 'Sign In'
              : mode === 'signup'
              ? 'Create Account'
              : 'Send Reset Email'}
          </button>

          {mode === 'reset' && (
            <button
              type="button"
              className="auth-link"
              onClick={() => switchMode('login')}
              disabled={isSubmitting}
            >
              Back to Sign In
            </button>
          )}
        </form>

        <LegalDocumentModal
          isOpen={showLegalModal !== null}
          onClose={() => setShowLegalModal(null)}
          documentType={showLegalModal || 'terms'}
        />
      </div>
    </div>
  );
}

